import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const projects = [
  {
    id: 1,
    title: "RESIDENCE MOIRA, GOA",
    description: "A tropical coastal home in Moira, Goa, where layered planes, filtered light, and lush landscape dissolve the boundary between inside and out.",
    image: "/images/projects/featured/moira.webp",
    route: "/projects/moira-goa",
  },
  {
    id: 2,
    title: "STUDIO SHARAN, NEW DELHI",
    description: "An open yet layered studio environment balancing transparency, focus, and collective work.",
    image: "/images/projects/featured/studio.webp",
    route: "/projects/studio-sharan",
  },
  {
    id: 3,
    title: "Residence 429",
    description: "A subterranean lounge where warm light, textured surfaces, and curated calm turn the basement into a destination.",
    image: "/images/projects/featured/basement.webp",
    route: "/projects/basement429-delhi",
  },
  {
    id: 4,
    title: "SIOLIM, GOA",
    description: "A contemporary coastal residence in Siolim, Goa, defined by layered concrete volumes, deep overhangs, and filtered tropical light.",
    image: "/images/projects/featured/siolim.webp",
    route: "/projects/siolim-goa",
  },
  {
    id: 5,
    title: "RESIDENCE 816, NEW DELHI",
    description: "A modern home quietly stitched into Delhi’s historic fabric, where greenery, light, and layered screens soften the edge between eras.",
    image: "/images/projects/featured/safdarjung.webp",
    route: "/projects/safdarjung-delhi",
  },
  {
    id: 6,
    title: "CAFE SNOW",
    description: "A light-filled café where warm timber, soft daylight, and gentle curves create a calm everyday ritual.",
    image: "/images/projects/featured/cafesnow.png",
    route: "/projects/soon-project",
  },
];

const HOVER_DELAY = 500;

const ProjectsSection = () => {
  const [activeId, setActiveId] = useState(null);
  const hoverTimeout = useRef(null);
  const navigate = useNavigate();

  const handleHoverStart = (id) => {
    hoverTimeout.current = setTimeout(() => {
      setActiveId(id);
    }, HOVER_DELAY);
  };

  const handleHoverEnd = () => {
    clearTimeout(hoverTimeout.current);
    setActiveId(null);
  };

  const handleProjectClick = (route) => {
    navigate(route);
  };

  return (
    <section id="projects" className="bg-white py-20 px-6 md:px-12">
      <h2 className="text-3xl md:text-4xl font-semibold text-left mb-14 tracking-wide">
        FEATURED PROJECTS
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
        {projects.map((project) => {
          const isActive = activeId === project.id;

          return (
            <motion.div
              key={project.id}
              className="group"
              onHoverStart={() => handleHoverStart(project.id)}
              onHoverEnd={handleHoverEnd}
            >
              {/* IMAGE WRAPPER — clickable */}
              <motion.div
                className="relative w-full aspect-video overflow-hidden cursor-pointer"
                onClick={() => handleProjectClick(project.route)}
                animate={{
                  borderRadius: isActive ? "20px" : "0px",
                }}
                transition={{
                  duration: 0.4,
                  ease: "easeOut",
                }}
              >
                {/* Image */}
                <motion.img
                  src={project.image}
                  alt={project.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  animate={{
                    filter: isActive ? "brightness(0.5)" : "brightness(1)",
                  }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                />

                {/* Title Overlay */}
                <motion.div
                  className="absolute inset-0 flex items-center justify-center text-white text-xl font-medium tracking-wide pointer-events-none"
                  animate={{
                    opacity: isActive ? 1 : 0,
                    y: isActive ? 0 : 10,
                  }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                >
                  {project.title}
                </motion.div>
              </motion.div>

              {/* Description */}
              <motion.div
                className="overflow-hidden"
                animate={{
                  height: isActive ? "auto" : 0,
                  opacity: isActive ? 1 : 0,
                }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              >
                <div className="pt-4 text-sm text-gray-700">
                  {project.description}
                </div>
              </motion.div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};

export default ProjectsSection;
